import React, { FC, useState } from 'react';
import { useModal } from 'store/modal';
import { FaStamp } from 'react-icons/fa';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { signDocAsEmployer } from 'routes/client/documents/helpers';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import useInvalidateQueries from 'utils/helpers/useInvalidateQueries';

interface IProps {
  children?: any;
  id: string;
  docHolderId: string;
  buttonText: string;
  handlePreview: (docId: string) => void;
  invalidate: () => void;
  size?: 'sm' | 'lg';
}

const VerifyAndSignDoc: FC<IProps> = ({
  id,
  docHolderId,
  buttonText,
  handlePreview,
  invalidate,
  size = 'sm',
}: IProps) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const invalidateQueries = useInvalidateQueries();

  // Verify and sign document
  const handleVerifyAndSign = (docId: string) => {
    showModal({
      type: 'loading',
      heading: t('documents.eSign'),
      content: t('documents.eSignSubtitle'),
    });
    signDocAsEmployer(
      docId,
      docHolderId,
      showModal,
      hideModal,
      () => invalidateQueries([], invalidate),
      'documents.table.verifiedAndSignedDocSuccessMsg',
      t
    );
  };

  // Confirm verification and sign
  const confirmVerifyAndSign = (docId: string) => {
    showModal({
      type: 'confirm',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('documents.table.confirmVerifyAndSignDocMsg')}</p>
          <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => handlePreviewDoc({ showModal, id: docId, setIsLoading })}
          >
            {t('documents.table.previewDoc')}
          </Button>
          <hr />
        </>
      ),
      onConfirm: () => handleVerifyAndSign(docId),
      confirmLabel: t('documents.table.confirmVerifyAndSignDocLabel'),
    });
  };

  return (
    <div className="d-inline-block ml-2 mb-2 mb-md-0">
      <TooltipWrapper
        id="tooltip-verify-and-sign"
        overlay={buttonText}
      >
        <Button
          type="button"
          btnType="info"
          size={size}
          onClick={() => confirmVerifyAndSign(id)}
        >
          <FaStamp size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default VerifyAndSignDoc;
